import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { FieldLabel, SectionCard } from '@/settings/shared/section-card';
import {
  usePracticeEPrescriptionLogin,
  useSavePracticeEPrescriptionLogin,
} from '@/settings/services';
import type { EPrescriptionLogin } from '@/types/settings-api';

/**
 * Practice eRx section — appended to the Practice Details tab.
 *
 * Mirrors Flutter `practice_widget/tab_widget/practice_details_tab.dart:666-786`
 * + the credentials editor at `:859-896`. Backed by:
 *   - GET  /api/Practice/EPrescriptionLogin?practiceId={id}   (single row)
 *   - POST /api/Practice/AddUpdateEPrescriptionLogin           (upsert by body.practiceId)
 *
 * Wire shape + behaviour confirmed by dev curls 2026-05-20 — see
 * `docs/CONVERSIONS/settings-practice-erx/CONVERSION_NOTES.md` for the
 * full audit trail. Highlights:
 *   - Backend does NOT enforce cross-toggle coupling — this component
 *     applies the Flutter coupling rules client-side.
 *   - POST sends the full state every time (no `if (!= null)` omission).
 *   - POST response is a JSON-quoted string of the row's id; the api-client
 *     JSON-parses it, but we ignore the return value (RQ invalidates the
 *     query and the next GET refreshes the UI).
 *
 * Two divergences from Flutter (both intentional, documented in
 * CONVERSION_NOTES §5 + §6.3):
 *   1. Flutter's credentials Edit-form Save is commented out at
 *      `practice_details_tab.dart:884` — clicking Save in Flutter doesn't
 *      actually persist credentials. **Plan A:** this React port wires
 *      Save to fire the real POST mutation. Dev-curl verified the same
 *      endpoint accepts credential writes (200 OK, returns row id).
 *   2. Flutter's `copyWith(ePrescriptionEnabled: null)` uses `??` to
 *      preserve the existing value (Flutter quirk where copyWith treats
 *      null as "keep current"). This component models the same behaviour
 *      explicitly by reading from the current row before each save.
 */
export interface PracticeErxSectionProps {
  practiceSetupId: string;
  canEdit: boolean;
}

const SUBTITLE_TEXT =
  'Submit your eClaim login details to enable eClaiming in the Practice Management app. ' +
  'You can request these details from your local regulatory office.';

export function PracticeErxSection({
  practiceSetupId,
  canEdit,
}: PracticeErxSectionProps) {
  const { data, isLoading } = usePracticeEPrescriptionLogin(practiceSetupId);
  const saveMutation = useSavePracticeEPrescriptionLogin();

  if (isLoading) {
    return (
      <SectionCard header="Practice features">
        <p className="text-sm text-[var(--color-text-muted)]">
          Loading practice features…
        </p>
      </SectionCard>
    );
  }

  // Empty-state defaults — covers null / undefined / {} responses
  // (per CONVERSION_NOTES §6.6, brand-new practices may return no row).
  const current: EPrescriptionLogin = {
    id: data?.id ?? null,
    providerId: data?.providerId ?? null,
    practiceId: data?.practiceId ?? practiceSetupId,
    practiceName: data?.practiceName ?? null,
    practiceImage: data?.practiceImage ?? null,
    username: data?.username ?? '',
    password: data?.password ?? '',
    ePrescriptionEnabled: data?.ePrescriptionEnabled ?? false,
    eClaimEnabled: data?.eClaimEnabled ?? false,
  };

  const eClaimOn = current.eClaimEnabled === true;
  const ePrescriptionOn = current.ePrescriptionEnabled === true;

  const fireSave = (next: EPrescriptionLogin, successMessage?: string) => {
    saveMutation.mutate(next, {
      onSuccess: () => {
        if (successMessage) toast.success(successMessage);
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Save failed');
      },
    });
  };

  // Flutter coupling rules (live wire matched, not the source's null-juggling):
  //   - eClaim ON  → eClaimEnabled: true (current ePrescription preserved)
  //   - eClaim OFF → eClaimEnabled: false, ePrescriptionEnabled: false,
  //                  username: "", password: "" (wipes credentials)
  //   - ePrescription ON  → ePrescriptionEnabled: true, eClaimEnabled: true
  //                         (force-on; ePrescription requires eClaim)
  //   - ePrescription OFF → ePrescriptionEnabled: false (current eClaim preserved)
  const handleEClaimToggle = (next: boolean) => {
    if (next) {
      fireSave(
        { ...current, eClaimEnabled: true },
        'eClaim enabled for this practice',
      );
    } else {
      fireSave(
        {
          ...current,
          eClaimEnabled: false,
          ePrescriptionEnabled: false,
          username: '',
          password: '',
        },
        'eClaim disabled for this practice',
      );
    }
  };

  const handleEPrescriptionToggle = (next: boolean) => {
    if (next) {
      fireSave(
        { ...current, ePrescriptionEnabled: true, eClaimEnabled: true },
        'ePrescription enabled for this practice',
      );
    } else {
      fireSave(
        { ...current, ePrescriptionEnabled: false },
        'ePrescription disabled for this practice',
      );
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard header="Practice features">
        <ToggleRow
          label="Enable eClaim"
          checked={eClaimOn}
          disabled={!canEdit || saveMutation.isPending}
          onChange={handleEClaimToggle}
        />
        <ToggleRow
          label="Enable ePrescription (via DHPO)"
          checked={ePrescriptionOn}
          disabled={!canEdit || saveMutation.isPending}
          onChange={handleEPrescriptionToggle}
        />
      </SectionCard>

      {eClaimOn && (
        <SectionCard header="eClaim Login Details">
          <p className="text-sm text-[var(--color-text-muted)] -mt-2">
            {SUBTITLE_TEXT}
          </p>
          <CredentialsEditor
            current={current}
            canEdit={canEdit}
            isSaving={saveMutation.isPending}
            onSave={(username, password) => {
              fireSave(
                { ...current, username, password },
                'eClaim credentials saved',
              );
            }}
          />
        </SectionCard>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm font-medium text-[var(--color-text-default,#151B20)]">
        {label}
      </span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
      />
    </div>
  );
}

function CredentialsEditor({
  current,
  canEdit,
  isSaving,
  onSave,
}: {
  current: EPrescriptionLogin;
  canEdit: boolean;
  isSaving: boolean;
  onSave: (username: string, password: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(current.username ?? '');
  const [password, setPassword] = useState(current.password ?? '');

  // Re-sync local state when the upstream record refetches (after a save
  // or a parent reload). Prevents stale local values from clobbering server
  // truth on the next edit attempt. Matches the Provider-side sibling
  // pattern in `eprescription-logins-section.tsx`.
  useEffect(() => {
    if (!isEditing) {
      setUsername(current.username ?? '');
      setPassword(current.password ?? '');
    }
  }, [current.username, current.password, isEditing]);

  const submitted = !!(current.username && current.username.length > 0);

  if (!isEditing) {
    return (
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-[var(--color-text-default,#151B20)]">
          {submitted ? 'Submitted' : 'Not submitted'}
        </span>
        {canEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
    );
  }

  const canSave =
    username.trim().length > 0 && password.trim().length > 0 && !isSaving;

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Username</FieldLabel>
        <Input
          value={username}
          placeholder="Enter username"
          disabled={!canEdit || isSaving}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>
      <div>
        <FieldLabel>Password</FieldLabel>
        <Input
          type="password"
          value={password}
          placeholder="Enter password"
          disabled={!canEdit || isSaving}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-3 justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isSaving}
          onClick={() => {
            setUsername(current.username ?? '');
            setPassword(current.password ?? '');
            setIsEditing(false);
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSave}
          onClick={() => {
            onSave(username, password);
            setIsEditing(false);
          }}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
